<template>
  <div>
    <XSearch
      v-model="searchFrom.no"
      class="mt-[0px] mb-[20px]"
      :placeholder="$t('qing-shu-ru-ding-dan-hao')"
      @search="handleOnSearch"
    />
    <a-table
      ref="tableRef"
      rowKey="id"
      :columns="columns"
      :data-source="list"
      :loading="loading"
      :pagination="pagination"
      :scroll="{ y: 'calc(100vh - 305px)' }"
      :expandRowByClick="true"
      @change="handleTableChange"
    >
      <template #bodyCell="{ column, text, record }">
        <div v-if="!record?.id" class="h-[22px]"></div>
        <div v-else-if="column.key === 'total_fee'">
          {{ formatPrice(record.total_fee).string }}
        </div>
        <div v-else-if="column.key === 'invoice_type'">
          {{ useDictItem('invoiceType', text)?.label }}
        </div>
        <div v-else-if="column.key === 'status'">
          <a-tag
            :color="useDictItem('invoiceStatus', text)?.color"
            class="!text-font-1 py-[1px] text-14 w-[72px] text-center rounded-[8px]"
          >
            {{ useDictItem('invoiceStatus', text)?.label }}
          </a-tag>
        </div>
        <div v-else-if="column.key === 'apply_time'">
          {{ timeFormat(text, 'YYYY.MM.DD HH:mm:ss') }}
        </div>
        <div v-else-if="column.key === 'finished_time'">
          {{
            record.status === INVOICE_STATUS.SUCCESS
              ? timeFormat(text, 'YYYY.MM.DD HH:mm:ss')
              : ''
          }}
        </div>
      </template>
      <template #expandedRowRender="{ record }">
        <div
          class="text-font-1 pl-[82px] flex gap-[10px] w-full overflow-hidden"
        >
          <div v-if="record.ident_code" style="min-width: 33%">
            {{
              $t(
                'na-shui-ren-shi-bie-hao-she-hui-tong-yi-she-hui-xin-yong-dai-ma'
              )
            }}：{{ record.ident_code }}
          </div>
          <div style="min-width: 20%">
            {{ $t('dian-zi-you-xiang') }}：{{ record.receiver_email }}
          </div>
          <div v-if="record.failed_reason" class="flex-1 flex flex-wrap">
            <div>{{ $t('shi-bai-yuan-yin') }}：</div>
            {{ record.failed_reason }}
          </div>
        </div>
      </template>
      <template #emptyText>
        <EmptyView class="h-auto" :class="{ invisible: loading }">
          <template #content> {{ $t('zan-wu-shu-ju') }} </template>
        </EmptyView>
      </template>
    </a-table>
  </div>
</template>
<script lang="ts" setup>
import type { TableProps } from 'ant-design-vue'
import FilterDropdown from '@/features/filterDropdown/index.vue'
import { invoiceOrders } from '~/api'
import { usePage } from '~/hooks/usePage'
import { useTime } from '~/hooks/useTime'

const { timeFormat } = useTime()

const props = defineProps<{
  userId?: number
  record?: any
  scrollY?: string
  pageSize?: number
}>()

const emit = defineEmits(['total'])

const route = useRoute()

const tableRef = ref()
const userTotal = ref(0)
const { $t } = useNuxtApp()
const searchFrom = reactive({ no: route.query.no })

watch(
  () => route.query.no,
  (cur, before) => {
    if (cur !== before && before) {
      searchFrom.no = cur
      reload()
    }
  }
)

const columns: any[] = [
  {
    title: $t('ding-dan-hao'),
    dataIndex: 'order_id',
    key: 'order_id',
    width: 160,
  },
  {
    title: $t('gong-si-tai-tou-ge-ren-xing-ming'),
    dataIndex: 'ident_name',
    key: 'ident_name',
    width: 200,
  },
  {
    title: $t('lei-xing'),
    dataIndex: 'invoice_type',
    key: 'invoice_type',
    filterDropdown: (props: any) =>
      h(FilterDropdown, {
        radio: true,
        type: 'invoiceType',
        ...props,
      }),
    width: 160,
  },
  {
    title: $t('jin-e'),
    dataIndex: 'total_fee',
    key: 'total_fee',
    sorter: true,
    width: 100,
  },
  {
    title: $t('zhuang-tai'),
    dataIndex: 'status',
    key: 'status',
    width: 160,
    filterDropdown: (props: any) =>
      h(FilterDropdown, {
        radio: true,
        type: 'invoiceStatus',
        ...props,
      }),
  },
  {
    title: $t('shen-qing-shi-jian'),
    dataIndex: 'apply_time',
    key: 'apply_time',
    sorter: true,
    defaultSortOrder: 'descend',
    width: 160,
  },
  {
    title: $t('kai-piao-shi-jian'),
    dataIndex: 'finished_time',
    key: 'finished_time',
    sorter: true,
    width: 160,
  },
]

const { list, total, page, page_size, loading, getList, reload } = usePage({
  api: async (params) => {
    const res: any = await invoiceOrders({
      o: '-create_time',
      ...params,
      many: 1,
      user_id: props.userId,
      order_id: searchFrom.no,
    })
    userTotal.value = res.data?.all_total_used_coins
    emit('total', res.data?.all_total_used_coins)
    return res
  },
  pageSize: props.pageSize || 20,
})

const handleOnSearch = (value: any) => {
  searchFrom.no = value
  reload()
}

// const dataSource = computed(() => {
//   return list.value
// })

const pagination = computed<any>(() => ({
  total: total.value,
  current: page.value,
  pageSize: page_size.value,
  showTotal: (total: string) => $t('gong-total-tiao', [total]),
  showSizeChanger: true,
  showQuickJumper: true,
}))

const handleTableChange: TableProps['onChange'] = (p, filters, sorter: any) => {
  page.value = p.current as number
  page_size.value = p.pageSize as number
  getList({
    page: p.current,
    page_size: p.pageSize,
    invoice_type: filters.invoice_type?.join(','),
    status: filters.status?.join(','),
    o: sorter.order
      ? `${sorter.order === 'ascend' ? '' : '-'}${sorter?.field}`
      : undefined,
  })
}

reload()
</script>
<style lang="scss" scoped>
// :deep(.ant-table-wrapper td.ant-table-column-sort) {
//   background-color: transparent;
// }
// :deep(.ant-table-wrapper .ant-table-thead th.ant-table-column-sort) {
//   background-color: #fafafa;
//   &:hover {
//     background-color: #f0f0f0;
//   }
// }

.line {
  @apply flex justify-between flex-nowrap;
}
:deep(.ant-table-placeholder) {
  .ant-table-cell {
    border-width: 0px !important;
  }
}
</style>
